export const WISH_LIST_SOURCE = 'From your wish list'
export const COLLECTION_SOURCE = 'From your collection'
export const CATALOG_SOURCE = 'Catalog discovery'
export function pairingSourceLabel(pairing, ownedOption) {
  return pairing?.source_label === WISH_LIST_SOURCE ? WISH_LIST_SOURCE : ownedOption ? COLLECTION_SOURCE : CATALOG_SOURCE
}
export const WEAR_SOURCE = 'From your wears'

export function includeWishList(preference, wearCount) {
  return typeof preference === 'boolean' ? preference : wearCount < 5
}

// Project catalog facts explicitly; ownership IDs and wear state never travel
// with this second candidate set.
export function wishListCandidate(row) {
  const catalog = row.catalog || row
  return { catalog_id: row.catalog_id, name: catalog.name, house: catalog.house,
    concentration: catalog.concentration, source_label: WISH_LIST_SOURCE }
}

export function wishListPartners(candidates, wishes, collection, enabled, anchorCatalogId) {
  if (!enabled) return []
  const owned = new Set(collection.map(row => row.catalog_id).filter(Boolean))
  const wanted = new Map(wishes.filter(row => row.catalog_id && !owned.has(row.catalog_id))
    .map(row => [row.catalog_id, wishListCandidate(row)]))
  return candidates.filter(row => row.catalog_id !== anchorCatalogId && wanted.has(row.catalog_id))
    .map(row => ({ ...wanted.get(row.catalog_id), compatibility_score: row.compatibility_score,
      reasoning: row.reasoning, application: row.application, role: row.role,
      volatility_bridge_note: row.volatility_bridge_note }))
}

// A wish row can move to the shelf in one tap only when the catalog already
// carries a usable concentration. Otherwise the caller must ask for it first.
export function wishRowToOwnershipItem(row) {
  const catalog = row?.catalog || row || {}
  const concentration = String(catalog.concentration || '').trim()
  if (!row?.catalog_id || !catalog.name || !catalog.house) return null
  if (!concentration || ['unknown', 'ambiguous'].includes(concentration.toLowerCase())) return null
  return { catalog_id: row.catalog_id, name: catalog.name, house: catalog.house, concentration, bottle_ml: null }
}

// Ledger rows written by the collection trigger when a wished bottle lands on
// the shelf. Missing ledger (migration not applied yet) reads as empty history.
export async function loadWishListAcquisitions(client, userId) {
  try {
    const { data, error } = await client.from('wish_list_acquisitions')
      .select('catalog_id,fragrance_id,wished_at,acquired_at,wish_source,catalog:catalog_id(id,name,house,concentration)')
      .eq('user_id', userId).order('acquired_at', { ascending: false }).limit(50)
    if (error) return []
    return Array.isArray(data) ? data : []
  } catch { return [] }
}

export async function removeAcquiredWishes(client, rows) {
  // The database trigger is authoritative. A failed secondary cleanup must not
  // turn an already committed acquisition into a retryable collection failure.
  for (const row of rows) {
    if (!row.user_id || !row.catalog_id) continue
    try {
      const { error } = await client.from('wish_list').delete().eq('user_id', row.user_id).eq('catalog_id', row.catalog_id)
      if (error) return false
    } catch { return false }
  }
  return true
}

// Wishlist candidates have a separate opt-in lane; do not repeat them as catalog fallbacks.
// Only exact catalog IDs exclude a candidate. Missing identity stays unclassified.
export function excludeWishListFallbacks(candidates, wishes, collection = []) {
  const owned=new Set(collection.map(row=>row.catalog_id).filter(Boolean).map(String))
  const wanted=new Set(wishes.map(row=>row.catalog_id).filter(Boolean).map(String))
  return candidates.filter(entry=>{const row=entry.recommendation || entry;const id=row.catalog_id && String(row.catalog_id);return !id || owned.has(id) || !wanted.has(id)})
}

// Move an owned bottle to the wish list. The database refuses a wish while any
// active row of that catalog identity is on the shelf, so every active row for
// this user + catalog is soft-removed first (grouped cards can hold several),
// then the wish is written directly and its own result checked. Refreshing
// local state is the caller's job and can never undo a committed move.
// On a failed wish write every removed row is restored; a failed restore is
// reported, never swallowed.
export async function moveBottleToWishList(client, { userId, catalogId }) {
  if (!userId || !catalogId) return { ok: false, error: 'This bottle has no catalog match, so it cannot go on the wish list.' }
  const removed = await client.from('fragrances').update({ deleted_at: new Date().toISOString() })
    .eq('user_id', userId).eq('catalog_id', catalogId).is('deleted_at', null).select('id')
  if (removed.error) return { ok: false, error: 'Could not move this bottle. Nothing was changed.' }
  const ids = (removed.data || []).map(row => row.id)
  const wish = await client.from('wish_list').upsert({ user_id: userId, catalog_id: catalogId, source: 'moved_from_collection' },
    { onConflict: 'user_id,catalog_id', ignoreDuplicates: true })
  if (!wish.error) return { ok: true, removedIds: ids }
  if (!ids.length) return { ok: false, error: 'Could not save this wish. Please try again.' }
  const restore = await client.from('fragrances').update({ deleted_at: null }).in('id', ids)
  if (restore.error) return { ok: false, restoreFailed: true, removedIds: ids,
    error: 'The wish could not be saved and the bottle could not be put back automatically. Please contact support.' }
  return { ok: false, error: 'Could not save this wish. Your bottle is still in your collection.' }
}
